import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label as LabelUI,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from '@artstorefronts/ui';
import { toast } from 'sonner';
import { PlusIcon, XIcon, SearchIcon } from 'lucide-react';
import { LABELS_QUERY } from '../graphql/queries';
import { useMutation, useQuery } from '@apollo/client';
import { Label } from '../types';
import { CREATE_LABEL } from '../graphql/mutations';

interface AddLabelProps {
  selectedLabels: Label[];
  onSelect: (label: Label) => void;
  onRemove: (label: Label) => void;
  label?: string;
}

function AddLabel({ selectedLabels, onSelect, onRemove, label }: AddLabelProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scrollAreaHeight, setScrollAreaHeight] = useState(0);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data: labelsData } = useQuery(LABELS_QUERY);
  const labels = labelsData?.labels || [];

  const [createLabel] = useMutation(CREATE_LABEL, {
    refetchQueries: [{ query: LABELS_QUERY }],
  });

  const isLabelSelected = (label: Label) => {
    return selectedLabels.some((l: Label) => l.id === label.id);
  };

  const filteredLabels = labels
    .filter((label: Label) => !isLabelSelected(label))
    .filter((label: Label) => (search ? label.name.includes(search) : true));

  const getMinHeight = () => {
    const itemCount = filteredLabels.length + (search && !labels.some((l: Label) => l.name === search) ? 1 : 0);
    return Math.min(itemCount * 44, 220);
  };

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    if (popoverContentRef.current && listRef.current) {
      const availableHeight = popoverContentRef.current.clientHeight - 70;
      const contentHeight = listRef.current.scrollHeight + 8;
      const maxHeight = Math.min(220, contentHeight);
      const newHeight = Math.max(contentHeight, availableHeight);

      setScrollAreaHeight(Math.min(maxHeight, newHeight));
    } else {
      setScrollAreaHeight(getMinHeight());
    }
  }, [search, labels, open]);

  const handleCreateLabel = async (labelName: string) => {
    try {
      const response = await createLabel({
        variables: {
          input: {
            name: labelName,
          },
        },
      });
      onSelect(response.data.createLabel);
      toast.success(t('labels.success.created'));
    } catch (error) {
      console.error('Error creating label:', error);
      toast.error(t('labels.error.create-failed'));
    }
  };

  const createLabelItem = (label: string) => {
    return (
      <button
        onClick={() => {
          handleCreateLabel(label);
          setOpen(false);
        }}
        className="font-bold cursor-pointer flex text-sm items-center p-2 bg-gray-100 rounded-md w-full mt-2 text-left hover:bg-gray-200 transition-colors duration-200 line-clamp-2 focus:outline-hidden focus:bg-gray-200"
      >
        <PlusIcon className="w-4 h-4 mr-2" />
        <span className="font-normal mr-1">{tc('create')}</span> {label}
      </button>
    );
  };

  return (
    <>
      <LabelUI className="mb-2">{label ?? t('labels.title')}</LabelUI>
      <div className="flex gap-2 flex-wrap">
        {selectedLabels.map((label) => (
          <div className="rounded-full bg-black text-sm h-9 text-white pl-3 py-1 flex items-center" key={label.id}>
            {label.name}
            <button className="p-2" onClick={() => onRemove(label)}>
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="lg" className="rounded-full" variant="outline">
              <PlusIcon className="size-4" /> {t('labels.add-label')}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            ref={popoverContentRef}
            className="overflow-hidden"
            style={{
              maxHeight: 'calc(var(--radix-popover-content-available-height)',
            }}
            side="bottom"
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="relative mb-2">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder={t('labels.search-placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-md pl-9"
                maxLength={50}
                autoComplete="off"
                spellCheck="false"
              />
            </div>
            <ScrollArea style={{ height: scrollAreaHeight }}>
              <div ref={listRef}>
                {search &&
                  labels.filter((label: Label) => label.name === search).length === 0 &&
                  createLabelItem(search)}
                {labels
                  .filter((label: Label) => !isLabelSelected(label))
                  .filter((label: Label) => (search ? label.name.includes(search) : true))
                  .map((label: Label) => (
                    <button
                      key={label.id}
                      onClick={() => {
                        onSelect(label);
                        setOpen(false);
                      }}
                      className="font-bold cursor-pointer flex text-sm items-center p-2 hover:bg-gray-100 rounded-md w-full mt-2 text-left focus:outline-hidden focus:bg-gray-100"
                    >
                      {label.name}
                    </button>
                  ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}

export default AddLabel;
